import { isGeometryProcessKey } from '@app/pages/admin/orchestration-process-keys.util';

/** Same module ids as ORCHESTRATION.md §3 and Masofa.Common OrchestrationProcessModuleClassifier. */

export type ProcessGroupId =
  | 'geometry'
  | 'satellite-products'
  | 'satellite-maps'
  | 'weather'
  | 'ai-inference'
  | 'other';

export type ProcessGroupFilterId = ProcessGroupId | 'all';

export interface ProcessGroupDefinition {
  id: ProcessGroupId;
  labelKey: string;
  icon: string;
}

export interface ProcessGroup<T> extends ProcessGroupDefinition {
  processes: T[];
}

export const PROCESS_GROUPS: readonly ProcessGroupDefinition[] = [
  { id: 'geometry', labelKey: 'Geometry', icon: 'pi pi-map-marker' },
  { id: 'satellite-products', labelKey: 'Satellite products', icon: 'pi pi-images' },
  { id: 'satellite-maps', labelKey: 'Satellite maps', icon: 'pi pi-map' },
  { id: 'weather', labelKey: 'Weather', icon: 'pi pi-cloud' },
  { id: 'ai-inference', labelKey: 'AI inference', icon: 'pi pi-sparkles' },
  { id: 'other', labelKey: 'Other', icon: 'pi pi-box' },
];

/** Catalog search matches id / i18n name in en-US, ru-RU and uz-Latn-UZ, not only the current UI language. */
export const PROCESS_GROUP_SEARCH_ALIASES: Record<ProcessGroupId, readonly string[]> = {
  geometry: ['Geometry', 'Геометрия', 'Geometriya'],
  'satellite-products': ['Satellite products', 'Спутниковые продукты', "Sun'iy yo'ldosh mahsulotlari"],
  'satellite-maps': ['Satellite maps', 'Спутниковые карты', "Sun'iy yo'ldosh xaritalari"],
  weather: ['Weather', 'Погода', 'Ob-havo'],
  'ai-inference': ['AI inference', 'ИИ-анализ', 'SI tahlili'],
  other: ['Other', 'Другое', 'Boshqa'],
};

export function processGroupSearchHaystack(group: Pick<ProcessGroupDefinition, 'id' | 'labelKey'>): string[] {
  return [group.id, group.labelKey, ...(PROCESS_GROUP_SEARCH_ALIASES[group.id] ?? [])];
}

const GROUP_ORDER: Record<ProcessGroupId, readonly string[]> = {
  geometry: [
    'geometry.resolve',
    'satellite.find-geometry',
  ],
  'satellite-products': [
    'satellite.find-products',
    'satellite.download-found-products',
    'satellite.download-product',
    'satellite.measure-download-backlog',
    'satellite.create-product-postprocessing',
    'satellite.catch-up-product-postprocessing',
    'satellite.reset-stale-work',
    'satellite.repair-products',
    'satellite.reconcile-product-storage',
  ],
  'satellite-maps': [
    'satellite.refresh-copernicus-metadata',
    'satellite.country-rgb-layers',
    'satellite.country-rgb-mosaic',
    'satellite.index-tiff-ingest',
    'satellite.index-tile-map-backfill',
    'satellite.calculate-season-indices',
    'satellite.calculate-index-layers',
    'satellite.backfill-season-indices',
  ],
  weather: [
    'weather.load-era-raw',
    'weather.calculate-era-reports',
    'weather.generate-era-tiles',
    'weather.maintain-era-raw',
    'weather.reconcile-era-reports',
    'weather.refresh-era-report-tiles',
    'weather.fill-era-sat',
    'weather.update-era-normalized',
    'weather.generate-era-deviation-tiles',
    'weather.generate-era-yearly-atd-tiles',
    'weather.fill-era-deviation-2020',
    'weather.load-ibm-historical',
    'weather.load-ugm-current',
    'weather.load-ugm-forecast',
  ],
  'ai-inference': [
    'ai.start-segmentation',
    'ai.identify-crops',
    'ai.calculate-yield-forecast',
    'ai.calculate-anomalies',
    'ai.transfer-yield-forecast-to-seasons',
    'ai.run-crop-classification',
  ],
  other: [
    'analitic.generate-farmer-reports',
    'crop.export-bid-templates',
    'analitic.submit-bids-to-qwen',
    'analitic.fetch-qwen-results',
    'analitic.collect-qwen-full-analysis',
    'analitic.start-qwen-analysis',
    'analitic.request-farmer-report-for-finished-bids',
    'analitic.request-farmer-report-for-satellite-anomalies',
    'analitic.collect-plant-diag-results',
    'crop.sync-cadastre-parcels',
    'uav.pull-sources',
    'orchestration.probe',
  ],
};

/** Product-pipeline event keys (`satellite.product-downloaded`) must not fall into satellite-maps. */
export function eventGroupId(eventKey: string): ProcessGroupId {
  const key = eventKey.trim();
  if (key.startsWith('geometry.') || key.endsWith('.find-geometry')) {
    return 'geometry';
  }
  if (key.startsWith('satellite.product')
    || key.startsWith('satellite.find-products')
    || key.startsWith('satellite.download')
    || key.startsWith('satellite.create-product')
    || key.startsWith('satellite.catch-up-product')) {
    return 'satellite-products';
  }
  return processGroupId(key);
}

export function processGroupId(processKey: string): ProcessGroupId {
  const key = processKey.trim();
  if (isGeometryProcessKey(key) || key.endsWith('.find-geometry')) {
    return 'geometry';
  }
  if (GROUP_ORDER['satellite-products'].includes(key)
    || /^(satellite\.(find-products|download|catch-up-product)|satellite\.create-product)/.test(key)) {
    return 'satellite-products';
  }
  if (key.startsWith('satellite.')) {
    return 'satellite-maps';
  }
  if (key.startsWith('weather.')) {
    return 'weather';
  }
  if (key.startsWith('ai.')) {
    return 'ai-inference';
  }
  return 'other';
}

export interface ProcessFlowGroupingInput {
  linear: boolean;
  nodes: ReadonlyArray<{ key: string }>;
  edges: ReadonlyArray<{ source: { key: string }; target: { key: string } }>;
}

/** Module of a schema = the process that starts it, not every node on the chain. */
export function processFlowStartKey(flow: ProcessFlowGroupingInput): string {
  if (!flow.nodes.length) {
    return '';
  }
  if (flow.linear) {
    return flow.nodes[0].key;
  }
  const incoming = new Set(
    flow.edges
      .filter(edge => edge.source.key !== edge.target.key)
      .map(edge => edge.target.key),
  );
  const starts = flow.nodes.filter(node => !incoming.has(node.key));
  const candidates = starts.length > 0 ? starts : flow.nodes;
  return [...candidates].sort((left, right) => compareProcessKeys(left.key, right.key))[0].key;
}

export function groupProcessFlows<T extends ProcessFlowGroupingInput>(
  flows: T[] | null | undefined,
): ProcessGroup<T>[] {
  return groupByProcessModule(flows, processFlowStartKey);
}

export function groupByProcessModule<T>(
  items: T[] | null | undefined,
  keyOf: (item: T) => string,
): ProcessGroup<T>[] {
  return groupCatalogItems(
    (items ?? []).map(item => ({ key: keyOf(item), item })),
    processGroupId,
  ).map(group => ({
    ...group,
    processes: group.processes.map(entry => entry.item),
  }));
}

export function groupProcesses<T extends { key: string }>(
  processes: T[] | null | undefined,
): ProcessGroup<T>[] {
  return groupCatalogItems(processes, processGroupId);
}

export function groupEvents<T extends { key: string }>(
  events: T[] | null | undefined,
): ProcessGroup<T>[] {
  return groupCatalogItems(events, eventGroupId);
}

function groupCatalogItems<T extends { key: string }>(
  items: T[] | null | undefined,
  classify: (key: string) => ProcessGroupId,
): ProcessGroup<T>[] {
  const buckets = new Map<ProcessGroupId, T[]>();
  for (const item of items ?? []) {
    const id = classify(item.key);
    const bucket = buckets.get(id) ?? [];
    bucket.push(item);
    buckets.set(id, bucket);
  }

  return PROCESS_GROUPS
    .map(group => ({
      ...group,
      processes: sortGroupProcesses(group.id, buckets.get(group.id) ?? []),
    }))
    .filter(group => group.processes.length > 0);
}

export function presentProcessGroups(processKeys: Iterable<string>): ProcessGroupDefinition[] {
  const present = new Set<ProcessGroupId>();
  for (const key of processKeys) {
    present.add(processGroupId(key));
  }
  return PROCESS_GROUPS.filter(group => present.has(group.id));
}

function sortGroupProcesses<T extends { key: string }>(
  groupId: ProcessGroupId,
  items: T[],
): T[] {
  return [...items].sort((left, right) => compareProcessKeys(left.key, right.key, groupId));
}

function compareProcessKeys(left: string, right: string, groupId = processGroupId(left)): number {
  if (groupId !== processGroupId(right)) {
    return PROCESS_GROUPS.findIndex(group => group.id === processGroupId(left))
      - PROCESS_GROUPS.findIndex(group => group.id === processGroupId(right));
  }
  const order = GROUP_ORDER[groupId];
  const leftIndex = order.indexOf(left);
  const rightIndex = order.indexOf(right);
  if (leftIndex === -1 && rightIndex === -1) {
    return left.localeCompare(right);
  }
  if (leftIndex === -1) {
    return 1;
  }
  if (rightIndex === -1) {
    return -1;
  }
  return leftIndex - rightIndex;
}
